import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

// Cache for logo to prevent unnecessary refetches
let logoCacheData: { data: string | null, timestamp: number } | null = null
const CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

export function useLogo() {
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const fetchLogo = async () => {
    // Check cache first
    if (logoCacheData && Date.now() - logoCacheData.timestamp < CACHE_DURATION) {
      setLogoUrl(logoCacheData.data)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')
    try {
      const { data, error: queryError } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'site_logo')
        .single()

      if (queryError) {
        if (queryError.code === 'PGRST116') {
          // No logo set yet
          const cachedValue = null
          logoCacheData = {
            data: cachedValue,
            timestamp: Date.now()
          }
          setLogoUrl(cachedValue)
          setLoading(false)
          return
        }
        throw queryError
      }

      const logoValue = data?.value || null
      
      // Cache the result
      logoCacheData = {
        data: logoValue,
        timestamp: Date.now()
      }
      
      setLogoUrl(logoValue)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch logo')
      setLogoUrl(null)
    } finally {
      setLoading(false)
    }
  }

  const updateLogo = async (logoUrl: string | null): Promise<boolean> => {
    try {
      const { error: upsertError } = await supabase
        .from('site_settings')
        .upsert(
          {
            key: 'site_logo',
            value: logoUrl || '',
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: 'key',
          }
        )

      if (upsertError) throw upsertError

      // Update cache immediately
      logoCacheData = {
        data: logoUrl,
        timestamp: Date.now()
      }

      setLogoUrl(logoUrl)
      return true
    } catch (err: any) {
      setError(err.message || 'Failed to update logo')
      return false
    }
  }

  useEffect(() => {
    fetchLogo()
  }, [])

  return {
    logoUrl,
    loading,
    error,
    updateLogo,
    refetch: fetchLogo,
  }
}
